import React from 'react'

/**
 * Logo Component - Reusable logo with PNG support
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.size - Size variant (sm, md, lg, xl)
 * @param {boolean} props.showText - Whether to show text alongside logo
 * @param {string} props.textSize - Text size variant
 */
const Logo = ({ 
  className = '', 
  size = 'md', 
  showText = true, 
  textSize = 'md',
  variant = 'default' // default, admin, mobile
}) => {
  // Size configurations
  const sizeConfig = {
    sm: { container: 'w-8 h-8', text: 'text-sm' },
    md: { container: 'w-12 h-12', text: 'text-lg' },
    lg: { container: 'w-16 h-16', text: 'text-2xl' },
    xl: { container: 'w-20 h-20', text: 'text-3xl' }
  }

  const textSizeConfig = {
    sm: 'text-xs',
    md: 'text-sm', 
    lg: 'text-lg',
    xl: 'text-xl'
  }

  const currentSize = sizeConfig[size] || sizeConfig.md
  const currentTextSize = textSizeConfig[textSize] || textSizeConfig.md

  // Variant configurations
  const variantConfig = {
    default: {
      containerClass: 'bg-white rounded-xl shadow-lg',
      textClass: 'text-white',
      subtextClass: 'text-green-100'
    },
    admin: {
      containerClass: 'bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg',
      textClass: 'text-gray-900',
      subtextClass: 'text-gray-600'
    },
    mobile: {
      containerClass: 'bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg',
      textClass: 'text-gray-900',
      subtextClass: 'text-gray-600'
    }
  }

  const currentVariant = variantConfig[variant] || variantConfig.default

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {/* Logo Container */}
      <div className={`${currentSize.container} ${currentVariant.containerClass} flex items-center justify-center relative overflow-hidden hover:scale-105 transition-transform duration-300`}>
        {/* SVG Logo - High quality vector logo */}
        <img 
          src="/maurifood-logo.svg" 
          alt="Maurifood Logo" 
          className="w-full h-full object-contain"
          onError={(e) => {
            // Fallback to CSS logo if SVG fails to load
            e.target.style.display = 'none'
            e.target.nextElementSibling.style.display = 'block'
          }}
        />
        
        {/* Fallback CSS Logo */}
        <div className="relative w-10 h-10 sm:w-12 sm:h-12" style={{ display: 'none' }}>
          {/* Letter M with Mauritian flag colors */}
          <div className="absolute left-0 top-0 w-1.5 h-full bg-red-600 rounded-sm"></div>
          <div className="absolute right-0 top-0 w-1.5 h-full bg-green-600 rounded-sm"></div>
          <div className="absolute left-1.5 top-0 w-1 h-8 bg-blue-800 transform rotate-12 origin-top-left rounded-sm"></div>
          <div className="absolute right-1.5 top-0 w-1 h-8 bg-yellow-500 transform -rotate-12 origin-top-right rounded-sm"></div>
          <div className="absolute left-1/2 top-2 w-1 h-6 bg-blue-800 transform -translate-x-1/2 rounded-sm"></div>
        </div>
      </div>

      {/* Text Content */}
      {showText && (
        <div>
          <h1 className={`${currentTextSize} font-bold ${currentVariant.textClass} drop-shadow-lg hover:scale-105 transition-transform duration-300`}>
            {variant === 'admin' ? 'Maurifood Admin' : 'Mauritian Groceries'}
          </h1>
          <p className={`text-xs sm:text-sm font-medium ${currentVariant.subtextClass}`}>
            {variant === 'admin' ? 'Management Panel' : 'Authentic Mauritian Food & Groceries'}
          </p>
        </div>
      )}
    </div>
  )
}

export default Logo
